/**
 * Supplier Search Redux Slice
 * State management for supplier search with polling support
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ProductStepId } from '@Constants/product.constants';
import type { RootState, Supplier } from '../types';
import { SupplierSearchApiService } from '../api/supplierSearchApi';
import { updateProduct } from './productSlice';
import {
  SupplierSearchState,
  SupplierSearchTask,
  StartSearchParams,
  CheckSearchStatusParams, SupplierSearchStatus,
} from '../types/supplierSearch.types';

// Constants
const POLLING_INTERVAL = 15000; // 15 seconds
const MAX_POLLING_ATTEMPTS = 200;
const STORAGE_KEY = 'supplier_search_state';

// Initial state
const initialState: SupplierSearchState = {
  searches: {},
  polling: {},
  suppliers: {},
  loading: {},
  error: {},
};

// Helper function to load state from localStorage
const loadStateFromStorage = (): Partial<SupplierSearchState> => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};

    const parsed = JSON.parse(stored);
    // Don't restore polling state or loading state
    return {
      searches: parsed.searches || {},
      suppliers: {},
      polling: {},
      loading: {},
      error: {},
    };
  } catch (error) {
    console.error('Failed to load supplier search state from storage:', error);
    return {};
  }
};

// Helper function to save state to localStorage
const saveStateToStorage = (state: SupplierSearchState) => {
  if (typeof window === 'undefined') return;

  try {
    // Only save searches, not polling or loading states
    const toSave = {
      searches: state.searches,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Failed to save supplier search state to storage:', error);
  }
};

// Async thunks
export const startSupplierSearch = createAsyncThunk(
  'supplierSearch/startSearch',
  async (params: StartSearchParams, { rejectWithValue, dispatch }) => {
    try {
      const task = await SupplierSearchApiService.startSupplierSearch(params.productId);
      return { productId: params.productId, task };
    } catch (error) {
      // Reset product to FindSuppliers stage on error
      await dispatch(updateProduct({
        productId: params.productId,
        productData: {
          roadmap_stage: ProductStepId.FindSuppliers
        }
      }));
      
      const message = error instanceof Error ? error.message : 'Failed to start supplier search';
      return rejectWithValue({ productId: params.productId, message });
    }
  }
);

export const checkSearchStatus = createAsyncThunk(
  'supplierSearch/checkStatus',
  async (params: CheckSearchStatusParams, { rejectWithValue, dispatch }) => {
    try {
      const task = await SupplierSearchApiService.getSearchStatus(params.taskId);
      
      if(task.status === SupplierSearchStatus.Failed){
        await dispatch(updateProduct({
          productId: params.productId,
          productData: {
            roadmap_stage: ProductStepId.FindSuppliers
          }
        }));
      }
      
      return { productId: params.productId, task };
    } catch (error) {
      // Reset product to FindSuppliers stage on error
      await dispatch(updateProduct({
        productId: params.productId,
        productData: {
          roadmap_stage: ProductStepId.FindSuppliers
        }
      }));
      
      const message = error instanceof Error ? error.message : 'Failed to check search status';
      return rejectWithValue({ productId: params.productId, message });
    }
  }
);

// Manual supplier management thunks
export const createManualSupplier = createAsyncThunk(
  'supplierSearch/createManualSupplier',
  async (params: {
    productId: string | number;
    supplierData: {
      name: string;
      email: string;
      supplier_info?: string;
      website?: string;
      phone?: string;
      wechat_number?: string;
    };
  }, { rejectWithValue }) => {
    try {
      const supplier = await SupplierSearchApiService.createManualSupplier(params.productId, params.supplierData);
      return { productId: params.productId.toString(), supplier };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create manual supplier';
      return rejectWithValue({ productId: params.productId.toString(), message });
    }
  }
);

export const updateManualSupplier = createAsyncThunk(
  'supplierSearch/updateManualSupplier',
  async (params: {
    productId: string | number;
    supplierId: string | number;
    supplierData: Partial<{
      name: string;
      email: string;
      supplier_info: string;
      website: string;
      phone: string;
      wechat_number: string;
    }>;
  }, { rejectWithValue }) => {
    try {
      const supplier = await SupplierSearchApiService.updateManualSupplier(
        params.productId,
        params.supplierId,
        params.supplierData
      );
      return { productId: params.productId.toString(), supplier };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update manual supplier';
      return rejectWithValue({ productId: params.productId.toString(), message });
    }
  }
);

export const deleteManualSupplier = createAsyncThunk(
  'supplierSearch/deleteManualSupplier',
  async (params: {
    productId: string | number;
    supplierId: string | number;
  }, { rejectWithValue }) => {
    try {
      await SupplierSearchApiService.deleteManualSupplier(params.productId, params.supplierId);
      return { productId: params.productId.toString(), supplierId: params.supplierId };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete manual supplier';
      return rejectWithValue({ productId: params.productId.toString(), message });
    }
  }
);

export const fetchProductSuppliers = createAsyncThunk(
  'supplierSearch/fetchProductSuppliers',
  async (productId: string | number, { rejectWithValue }) => {
    try {
      const response = await SupplierSearchApiService.getProductSuppliers(productId);
      // Normalize TaskSupplier records from the API to the Supplier shape stored in state
      const suppliers: Supplier[] = response.suppliers.map((supplier) => ({
        ...supplier,
        product_id: Number(productId),
        external_supplier_id: supplier.external_supplier_id ?? '',
        name: supplier.name ?? '',
        address: supplier.address ?? '',
        emails: supplier.emails ?? [],
        phones: supplier.phone ? [supplier.phone] : [],
        website: supplier.website ?? '',
        founded_year: supplier.founded_year ?? 0,
        supplier_info: supplier.supplier_info ?? '',
        wechat_number: supplier.wechat_number ?? '',
        source: supplier.is_manual ? 'manual' : 'auto',
      }));
      return { productId: productId.toString(), suppliers };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch product suppliers';
      return rejectWithValue({ productId: productId.toString(), message });
    }
  }
);

// Slice
export const supplierSearchSlice = createSlice({
  name: 'supplierSearch',
  initialState: { ...initialState, ...loadStateFromStorage() },
  reducers: {
    startPolling: (state, action: PayloadAction<{ productId: number }>) => {
      const productId = action.payload.productId.toString();
      state.polling[productId] = {
        active: true,
        attempts: 0,
        maxAttempts: MAX_POLLING_ATTEMPTS,
        interval: POLLING_INTERVAL,
      };
    },

    stopPolling: (state, action: PayloadAction<{ productId: number }>) => {
      const productId = action.payload.productId.toString();
      if (state.polling[productId]) {
        state.polling[productId].active = false;
      }
    },

    incrementPollingAttempts: (state, action: PayloadAction<{ productId: number }>) => {
      const productId = action.payload.productId.toString();
      if (state.polling[productId]) {
        state.polling[productId].attempts += 1;
      }
    },

    clearSearch: (state, action: PayloadAction<{ productId: number }>) => {
      const productId = action.payload.productId.toString();
      delete state.searches[productId];
      delete state.polling[productId];
      delete state.loading[productId];
      delete state.error[productId];
      saveStateToStorage(state);
    },

    clearAllSearches: (state) => {
      state.searches = {};
      state.polling = {};
      state.loading = {};
      state.error = {};
      saveStateToStorage(state);
    },
  },

  extraReducers: (builder) => {
    // Start supplier search
    builder.addCase(startSupplierSearch.pending, (state, action) => {
      const productId = action.meta.arg.productId.toString();
      state.loading[productId] = true;
      state.error[productId] = null;
    });

    builder.addCase(startSupplierSearch.fulfilled, (state, action) => {
      const productId = action.payload.productId.toString();
      state.loading[productId] = false;
      state.searches[productId] = action.payload.task;
      state.polling[productId] = {
        active: true,
        attempts: 0,
        maxAttempts: MAX_POLLING_ATTEMPTS,
        interval: POLLING_INTERVAL,
      };
      saveStateToStorage(state);
    });

    builder.addCase(startSupplierSearch.rejected, (state, action) => {
      const payload = action.payload as { productId: number; message: string } | undefined;
      const productId = payload?.productId?.toString() || action.meta.arg.productId.toString();
      state.loading[productId] = false;
      state.error[productId] = payload?.message || 'Unknown error';
    });

    // Check search status
    builder.addCase(checkSearchStatus.pending, (state, action) => {
      const productId = action.meta.arg.productId.toString();
      state.loading[productId] = true;
    });

    builder.addCase(checkSearchStatus.fulfilled, (state, action) => {
      const productId = action.payload.productId.toString();
      state.loading[productId] = false;
      state.searches[productId] = action.payload.task;
      state.error[productId] = null;


      // Stop polling if search is completed or failed
      if (
        action.payload.task.status === SupplierSearchStatus.Completed ||
        action.payload.task.status === SupplierSearchStatus.Failed
      ) {
        if (state.polling[productId]) {
          state.polling[productId].active = false;
        }
      }

      saveStateToStorage(state);
    });

    builder.addCase(checkSearchStatus.rejected, (state, action) => {
      const payload = action.payload as { productId: number; message: string } | undefined;
      const productId = payload?.productId?.toString() || action.meta.arg.productId.toString();
      state.loading[productId] = false;
      state.error[productId] = payload?.message || 'Unknown error';
    });

    // Create manual supplier
    builder.addCase(createManualSupplier.pending, (state, action) => {
      const productId = action.meta.arg.productId.toString();
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = true;
      state.suppliers[productId].error = null;
    });

    builder.addCase(createManualSupplier.fulfilled, (state, action) => {
      const productId = action.payload.productId;
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = false;
      state.suppliers[productId].list.push(action.payload.supplier);
    });

    builder.addCase(createManualSupplier.rejected, (state, action) => {
      const payload = action.payload as { productId: string; message: string } | undefined;
      const productId = payload?.productId || action.meta.arg.productId.toString();
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = false;
      state.suppliers[productId].error = payload?.message || 'Unknown error';
    });

    // Update manual supplier
    builder.addCase(updateManualSupplier.pending, (state, action) => {
      const productId = action.meta.arg.productId.toString();
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = true;
      state.suppliers[productId].error = null;
    });

    builder.addCase(updateManualSupplier.fulfilled, (state, action) => {
      const productId = action.payload.productId;
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = false;
      const index = state.suppliers[productId].list.findIndex(
        s => s.id === action.payload.supplier.id
      );
      if (index !== -1) {
        state.suppliers[productId].list[index] = action.payload.supplier;
      }
    });

    builder.addCase(updateManualSupplier.rejected, (state, action) => {
      const payload = action.payload as { productId: string; message: string } | undefined;
      const productId = payload?.productId || action.meta.arg.productId.toString();
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = false;
      state.suppliers[productId].error = payload?.message || 'Unknown error';
    });

    // Delete manual supplier
    builder.addCase(deleteManualSupplier.pending, (state, action) => {
      const productId = action.meta.arg.productId.toString();
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = true;
      state.suppliers[productId].error = null;
    });

    builder.addCase(deleteManualSupplier.fulfilled, (state, action) => {
      const productId = action.payload.productId;
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = false;
      state.suppliers[productId].list = state.suppliers[productId].list.filter(
        s => s.id !== action.payload.supplierId
      );
    });

    builder.addCase(deleteManualSupplier.rejected, (state, action) => {
      const payload = action.payload as { productId: string; message: string } | undefined;
      const productId = payload?.productId || action.meta.arg.productId.toString();
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = false;
      state.suppliers[productId].error = payload?.message || 'Unknown error';
    });

    // Fetch product suppliers
    builder.addCase(fetchProductSuppliers.pending, (state, action) => {
      const productId = action.meta.arg.toString();
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = true;
      state.suppliers[productId].error = null;
    });

    builder.addCase(fetchProductSuppliers.fulfilled, (state, action) => {
      const productId = action.payload.productId;
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = false;
      state.suppliers[productId].list = action.payload.suppliers;
    });

    builder.addCase(fetchProductSuppliers.rejected, (state, action) => {
      const payload = action.payload as { productId: string; message: string } | undefined;
      const productId = payload?.productId || action.meta.arg.toString();
      if (!state.suppliers[productId]) {
        state.suppliers[productId] = { list: [], loading: false, error: null };
      }
      state.suppliers[productId].loading = false;
      state.suppliers[productId].error = payload?.message || 'Unknown error';
    });
  },
});

// Actions
export const {
  startPolling,
  stopPolling,
  incrementPollingAttempts,
  clearSearch,
  clearAllSearches,
} = supplierSearchSlice.actions;

// Selectors
export const selectSupplierSearch = (state: RootState, productId: number) =>
  state.supplierSearch.searches[productId.toString()];

export const selectIsPolling = (state: RootState, productId: number) =>
  state.supplierSearch.polling[productId.toString()]?.active || false;

export const selectPollingAttempts = (state: RootState, productId: number) =>
  state.supplierSearch.polling[productId.toString()]?.attempts || 0;

export const selectSearchLoading = (state: RootState, productId: number) =>
  state.supplierSearch.loading[productId.toString()] || false;

export const selectSearchError = (state: RootState, productId: number) =>
  state.supplierSearch.error[productId.toString()] || null;

export const selectAllActiveSearches = (state: RootState) => {
  const activeSearches: { productId: number; task: SupplierSearchTask }[] = [];

  Object.entries(state.supplierSearch.searches).forEach(([productId, task]) => {
    if (task.status === 'pending' || task.status === 'processing' || task.status === 'in_progress') {
      activeSearches.push({ productId: parseInt(productId), task });
    }
  });

  return activeSearches;
};

// Selector to check if a specific product has an active search
export const selectIsProductSearchActive = (state: RootState, productId: number) => {
  const task = state.supplierSearch.searches[productId.toString()];
  if (!task) return false;

  return task.status === 'pending' || task.status === 'processing' || task.status === 'in_progress';
};

// Supplier selectors
export const selectProductSuppliers = (state: RootState, productId: string | number) =>
  state.supplierSearch.suppliers[productId.toString()]?.list || [];

export const selectSuppliersLoading = (state: RootState, productId: string | number) =>
  state.supplierSearch.suppliers[productId.toString()]?.loading || false;

export const selectSuppliersError = (state: RootState, productId: string | number) =>
  state.supplierSearch.suppliers[productId.toString()]?.error || null;

export default supplierSearchSlice.reducer;

